import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, Badge, Space, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DashboardOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  CommentOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  GlobalOutlined,
  SyncOutlined,
  WifiOutlined,
  DisconnectOutlined,
} from '@ant-design/icons';
import { syncOfflineQueue } from '../services/api';

const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;

interface PageLayoutProps {
  children: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  // 使用固定的顏色值，而不是從主題中獲取
  const colorBgContainer = '#ffffff';
  const borderRadiusLG = 8;

  // 監聽網絡狀態變化
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 同步離線數據
  const handleSync = async () => {
    if (!isOnline) return;

    setIsSyncing(true);
    try {
      await syncOfflineQueue();
    } finally {
      setIsSyncing(false);
    }
  };

  // 切換語言
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  // 菜單項
  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: t('menu.dashboard'),
    },
    {
      key: '/stores',
      icon: <ShopOutlined />,
      label: t('menu.stores'),
    },
    {
      key: '/purchase-orders',
      icon: <ShoppingCartOutlined />,
      label: t('menu.purchaseOrders'),
    },
    {
      key: '/review-tasks',
      icon: <CommentOutlined />,
      label: t('menu.reviewTasks'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: t('menu.settings'),
    },
  ];

  // 語言選項
  const languageItems = [
    {
      key: 'zh-TW',
      label: '繁體中文',
    },
    {
      key: 'en-US',
      label: 'English',
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="light">
        <div style={{ height: 32, margin: 16, textAlign: 'center' }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {!collapsed ? t('app.title') : 'OEMS'}
          </Typography.Title>
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: 0, background: colorBgContainer }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 16 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 64, height: 64 }}
            />
            <Space>
              <Badge
                status={isOnline ? 'success' : 'error'}
                text={
                  <Space>
                    {isOnline ? <WifiOutlined /> : <DisconnectOutlined />}
                    <Text>{isOnline ? t('common.online') : t('common.offline')}</Text>
                  </Space>
                }
              />
              <Button
                icon={<SyncOutlined spin={isSyncing} />}
                onClick={handleSync}
                disabled={!isOnline || isSyncing}
              >
                {t('common.sync')}
              </Button>
              <Dropdown
                menu={{
                  items: languageItems,
                  onClick: ({ key }) => handleLanguageChange(key),
                }}
              >
                <Button icon={<GlobalOutlined />}>
                  {i18n.language === 'zh-TW' ? '繁體中文' : 'English'}
                </Button>
              </Dropdown>
            </Space>
          </div>
        </Header>
        <Content style={{ margin: '24px 16px' }}>
          <div
            style={{
              padding: 24,
              minHeight: 360,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            {children}
          </div>
        </Content>
        <Footer style={{ textAlign: 'center' }}>{t('app.footer')}</Footer>
      </Layout>
    </Layout>
  );
};

export default PageLayout;
